import { Dialog, TovFormTextField, msg, useTovForm } from '@tov-plus/ui';

import {
  galleryItemLabel,
  resolveGalleryAssetUrl,
  type GuestGalleryItem,
} from './galleryHelpers';

interface GalleryLightboxProps {
  items: GuestGalleryItem[];
  openIndex: number | null;
  favoriteItemIds: Set<string>;
  pendingFavoriteItemIds: Set<string>;
  commentDraft: string;
  pendingReactionItemIds: Set<string>;
  savingCommentItemIds: Set<string>;
  canUseGuestIdentity?: boolean;
  onOpenChange: (index: number | null) => void;
  onToggleFavorite: (itemId: string) => void;
  onCommentDraftChange: (value: string) => void;
  onSubmitComment: (itemId: string) => void;
  onToggleReaction: (
    itemId: string,
    reactionKind: 'heart' | 'celebrate',
    active: boolean,
  ) => void;
  onCreateSharePackage: (itemId: string) => void;
  isCreatingSharePackage: boolean;
}

export function GalleryLightbox({
  items,
  openIndex,
  favoriteItemIds,
  pendingFavoriteItemIds,
  commentDraft,
  pendingReactionItemIds,
  savingCommentItemIds,
  canUseGuestIdentity = true,
  onOpenChange,
  onToggleFavorite,
  onCommentDraftChange,
  onSubmitComment,
  onToggleReaction,
  onCreateSharePackage,
  isCreatingSharePackage,
}: GalleryLightboxProps) {
  const commentForm = useTovForm({
    defaultValues: {
      comment: commentDraft,
    },
    onSubmit: () => {
      if (item) {
        onSubmitComment(item.galleryItem.id);
      }
    },
  });
  const item = openIndex === null ? null : (items[openIndex] ?? null);
  const imageUrl = item
    ? resolveGalleryAssetUrl(item.displayUrl ?? item.assetUrl)
    : null;
  const hasPrevious = openIndex !== null && openIndex > 0;
  const hasNext = openIndex !== null && openIndex < items.length - 1;
  const isFavorite = item ? favoriteItemIds.has(item.galleryItem.id) : false;
  const isFavoritePending = item
    ? pendingFavoriteItemIds.has(item.galleryItem.id)
    : false;
  const isReactionPending = item
    ? pendingReactionItemIds.has(item.galleryItem.id)
    : false;
  const isCommentSaving = item
    ? savingCommentItemIds.has(item.galleryItem.id)
    : false;
  const heartReaction = item
    ? item.reactions.find((reaction) => reaction.reaction_kind === 'heart')
    : null;
  const celebrateReaction = item
    ? item.reactions.find((reaction) => reaction.reaction_kind === 'celebrate')
    : null;

  return (
    <Dialog
      open={item !== null}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          onOpenChange(null);
        }
      }}
      title={item ? galleryItemLabel(item) : undefined}
      contentClassName="gallery-lightbox"
      bodyClassName="gallery-lightbox__body"
    >
      {item && imageUrl ? (
        <>
          <div className="gallery-lightbox__stage">
            {hasPrevious ? (
              <button
                type="button"
                className="gallery-lightbox__nav gallery-lightbox__nav--prev"
                aria-label={msg('pages.gallery_page.gallery_previous_photo')}
                onClick={() => onOpenChange((openIndex ?? 0) - 1)}
              >
                <span aria-hidden="true">‹</span>
              </button>
            ) : null}

            <img
              className="gallery-lightbox__image"
              src={imageUrl}
              alt={galleryItemLabel(item)}
            />

            {hasNext ? (
              <button
                type="button"
                className="gallery-lightbox__nav gallery-lightbox__nav--next"
                aria-label={msg('pages.gallery_page.gallery_next_photo')}
                onClick={() => onOpenChange((openIndex ?? 0) + 1)}
              >
                <span aria-hidden="true">›</span>
              </button>
            ) : null}
          </div>

          <p className="gallery-lightbox__meta">
            {msg('pages.gallery_page.gallery_shared_by')}{' '}
            <strong>
              {item.galleryItem.uploaderDisplayName ??
                item.galleryItem.uploaderKind}
            </strong>
            {item.event?.displayName ? ` · ${item.event.displayName}` : ''}
          </p>
          <p className="gallery-lightbox__actions">
            {canUseGuestIdentity ? (
              <button
                type="button"
                disabled={isFavoritePending}
                onClick={() => onToggleFavorite(item.galleryItem.id)}
              >
                {isFavorite
                  ? msg('pages.gallery_page.gallery_remove_favorite')
                  : msg('pages.gallery_page.gallery_add_favorite')}
              </button>
            ) : null}
            <a
              download
              href={item.downloadUrl}
              rel="noreferrer"
              target="_blank"
            >
              {msg('pages.gallery_page.gallery_download_original')}
            </a>
            {canUseGuestIdentity ? (
              <button
                type="button"
                disabled={isCreatingSharePackage}
                onClick={() => onCreateSharePackage(item.galleryItem.id)}
              >
                {isCreatingSharePackage
                  ? msg('pages.gallery_page.gallery_preparing_share')
                  : msg('general.actions.share')}
              </button>
            ) : null}
          </p>
          {canUseGuestIdentity ? (
            <div className="gallery-engagement">
              <div className="gallery-engagement__reactions">
                <button
                  type="button"
                  className={
                    heartReaction?.reacted_by_current_actor
                      ? 'gallery-engagement__reaction gallery-engagement__reaction--active'
                      : 'gallery-engagement__reaction'
                  }
                  disabled={isReactionPending}
                  onClick={() =>
                    onToggleReaction(
                      item.galleryItem.id,
                      'heart',
                      !(heartReaction?.reacted_by_current_actor ?? false),
                    )
                  }
                >
                  {msg('pages.gallery_page.gallery_reaction_heart')}{' '}
                  {heartReaction?.count ?? 0}
                </button>
                <button
                  type="button"
                  className={
                    celebrateReaction?.reacted_by_current_actor
                      ? 'gallery-engagement__reaction gallery-engagement__reaction--active'
                      : 'gallery-engagement__reaction'
                  }
                  disabled={isReactionPending}
                  onClick={() =>
                    onToggleReaction(
                      item.galleryItem.id,
                      'celebrate',
                      !(celebrateReaction?.reacted_by_current_actor ?? false),
                    )
                  }
                >
                  {msg('pages.gallery_page.gallery_reaction_celebrate')}{' '}
                  {celebrateReaction?.count ?? 0}
                </button>
              </div>
              <div className="gallery-comments">
                <strong>{msg('pages.gallery_page.gallery_comments')}</strong>
                {item.comments.length > 0 ? (
                  <ul className="gallery-comments__list">
                    {item.comments.map((comment) => (
                      <li key={comment.id}>{comment.body}</li>
                    ))}
                  </ul>
                ) : (
                  <p>{msg('pages.gallery_page.gallery_no_comments')}</p>
                )}
                <form
                  className="gallery-comments__form"
                  onSubmit={async (event) => {
                    event.preventDefault();
                    await commentForm.handleSubmit();
                  }}
                >
                  <TovFormTextField
                    form={commentForm}
                    formatValue={() => commentDraft}
                    name="comment"
                    placeholder={msg(
                      'pages.gallery_page.gallery_comment_placeholder',
                    )}
                    onValueChange={onCommentDraftChange}
                  />
                  <button type="submit" disabled={isCommentSaving}>
                    {isCommentSaving
                      ? msg('components.shell.components_shell_sending')
                      : msg('pages.help_page.pages_help_page_send_follow_up')}
                  </button>
                </form>
              </div>
            </div>
          ) : null}
        </>
      ) : null}
    </Dialog>
  );
}
